"use client";

import * as React from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";

import { cn } from "@workspace/ui/lib/utils";

/**
 * IndismissableSheet - A sheet component that cannot be dismissed by the user.
 * No close button, no escape key, no clicking outside to close (except header bar).
 * Can only be closed programmatically by changing the `open` prop.
 */
function IndismissableSheet({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return (
    <SheetPrimitive.Root
      data-slot="indismissable-sheet"
      modal={false}
      {...props}
    />
  );
}

function IndismissableSheetPortal({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return (
    <SheetPrimitive.Portal data-slot="indismissable-sheet-portal" {...props} />
  );
}

function IndismissableSheetOverlay({
  className,
  disableAnimations = false,
  disableEnterAnimation = false,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay> & {
  disableAnimations?: boolean;
  disableEnterAnimation?: boolean;
}) {
  return (
    <SheetPrimitive.Overlay
      data-slot="indismissable-sheet-overlay"
      className={cn(
        "fixed z-40 bg-black/50 pointer-events-none",
        // Overlay covers everything except the header bar (64px from top)
        "top-16 left-0 right-0 bottom-0",
        // Only apply animations if not disabled
        !disableAnimations &&
          !disableEnterAnimation &&
          "data-[state=open]:animate-in data-[state=open]:fade-in-0",
        !disableAnimations &&
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
        className,
      )}
      {...props}
    />
  );
}

function IndismissableSheetContent({
  className,
  children,
  side = "bottom",
  disableAnimations = false,
  disableEnterAnimation = false,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: "top" | "right" | "bottom" | "left";
  disableAnimations?: boolean;
  disableEnterAnimation?: boolean;
}) {
  return (
    <IndismissableSheetPortal>
      <IndismissableSheetOverlay
        disableAnimations={disableAnimations}
        disableEnterAnimation={disableEnterAnimation}
      />
      <SheetPrimitive.Content
        data-slot="indismissable-sheet-content"
        // Prevent closing via escape key
        onEscapeKeyDown={(e) => e.preventDefault()}
        // Don't prevent pointer down or interact outside - let overlay handle it
        // This allows clicks on header to pass through
        className={cn(
          "bg-background fixed z-40 flex flex-col gap-4 shadow-lg pointer-events-auto",
          // Transition always applied unless all animations disabled
          !disableAnimations && "transition ease-in-out",
          // Exit animations - always enabled unless disableAnimations is true
          !disableAnimations && [
            "data-[state=closed]:animate-out data-[state=closed]:duration-300",
            side === "right" && "data-[state=closed]:slide-out-to-right",
            side === "left" && "data-[state=closed]:slide-out-to-left",
            side === "top" && "data-[state=closed]:slide-out-to-top",
            side === "bottom" && "data-[state=closed]:slide-out-to-bottom",
          ],
          // Enter animations - disabled if disableEnterAnimation OR disableAnimations
          !disableAnimations &&
            !disableEnterAnimation && [
              "data-[state=open]:animate-in data-[state=open]:duration-500",
              side === "right" && "data-[state=open]:slide-in-from-right",
              side === "left" && "data-[state=open]:slide-in-from-left",
              side === "top" && "data-[state=open]:slide-in-from-top",
              side === "bottom" && "data-[state=open]:slide-in-from-bottom",
            ],
          // Position classes (always applied)
          side === "right" &&
            "inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm",
          side === "left" &&
            "inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm",
          side === "top" && "inset-x-0 top-0 h-auto border-b",
          side === "bottom" && "inset-x-0 bottom-0 h-auto",
          className,
        )}
        {...props}
      >
        {children}
        {/* No close button */}
      </SheetPrimitive.Content>
    </IndismissableSheetPortal>
  );
}

function IndismissableSheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="indismissable-sheet-title"
      className={cn("text-lg font-semibold", className)}
      {...props}
    />
  );
}

function IndismissableSheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="indismissable-sheet-description"
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  );
}

const IndismissableSheetVisuallyHidden = VisuallyHidden.Root;

export {
  IndismissableSheet,
  IndismissableSheetPortal,
  IndismissableSheetOverlay,
  IndismissableSheetContent,
  IndismissableSheetTitle,
  IndismissableSheetDescription,
  IndismissableSheetVisuallyHidden,
};
